import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Tag as DefaultTag } from 'primereact/tag';
import { Messages } from 'primereact/messages';
import { ProgressBar } from 'primereact/progressbar';
import { useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputSwitch } from 'primereact/inputswitch';
import { PageTitle } from '../components/Headings';
import { getUserById, UserColumns } from '../services/user';
import { getUserByEmail as getUserInfo } from '../common/graph';
import AddUserRoleModal from '../components/admin/AddUserRoleModal';
import { useRemoveUserRole, useUserUpdateStatus } from '../services/mutations';
import { isManagerRole, isOperationSustainability } from '../common/roles';
import { Role } from '../services/model';
import { ActionsWrapper } from '../components/DataTable/common';

const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-bottom: 16px;
`;
const InfoGrid = styled.div`
  display: grid;
  width: fit-content;
  padding: 16px;
  grid-template-columns: max-content max-content;
  row-gap: 16px;
  column-gap: 16px;
  background-color: ${({ theme }) => theme.colors.bg_gray_light};
  border-left: 5px solid ${({ theme }) => theme.colors.bg_gray};
  margin-bottom: 16px;
`;

const Tag = styled(DefaultTag)`
  margin-right: 6px;
  margin-bottom: 2px;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LabelWrapper = styled.div`
  margin-bottom: 4px;
`;

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

async function getUserDetail(userId: string) {
  const user = await getUserById(userId);
  const userInfo = await getUserInfo(user.email);
  return { ...user, userInfo };
}

function UserDetailPage() {
  const [rolesInfo, setRolesInfo] = useState(null);
  const { userId } = useParams<string>();
  const [isStatusActive, setIsStatusActive] = useState<boolean | null>(null); // Initialize to null to indicate loading state
  const [isStatusAdmin, setIsStatusAdmin] = useState<boolean | null>(null); // Initialize to null to indicate loading state

  const { isLoading, data, refetch } = useQuery(['userDetail', userId], () => getUserDetail(userId!), {
    onSuccess: (resp) => {
      if (resp) {
        setIsStatusActive(resp.isActive);
        setIsStatusAdmin(resp.isAdmin);

        const proccessData = resp.roles.flatMap((obj) =>
          obj.placeCodes.map((value) => ({ name: obj.name, grant: obj.grant.name, placeCode: value })),
        );

        setRolesInfo({
          isManagerIn: proccessData.filter((i) => isManagerRole(i.grant)).map((i) => i.placeCode),
          isOperationSustIn: proccessData.filter((i) => isOperationSustainability(i.grant)).map((i) => i.placeCode),
        });
      }
    },
  });
  const [isAddUserRoleModalOpen, setIsAddUserRoleModalOpen] = useState(false);
  const messages = useRef<Messages>(null);
  const removeUserRole = useRemoveUserRole();
  const updateUserStatus = useUserUpdateStatus();

  const removeRole = (rowData: any) => {
    removeUserRole.mutate({ UserId: Number(userId), RoleId: rowData.id });
  };

  /**
   * Handles the change of user status (active/inactive and admin/non-admin).
   * @param column - The column that is being updated (IsActive or IsAdmin).
   * @param newValue - The new value for the status being updated.
   */
  /**
   * Handles the change of user status (active/inactive and admin/non-admin).
   * @param column - The column that is being updated (IsActive or IsAdmin).
   * @param newValue - The new value for the status being updated.
   */
  const handleStatusChange = (column: UserColumns, newValue: boolean) => {
    const statusUpdate = {
      userId: Number(userId), // Ensure userId is a number

      isActive: column === UserColumns.IsActive ? newValue : isStatusActive!,
      isAdmin: column === UserColumns.IsAdmin ? newValue : isStatusAdmin!,
    };

    // Update the user status
    updateUserStatus.mutate(statusUpdate, {
      onSuccess: () => {
        if (column === UserColumns.IsActive) {
          setIsStatusActive(newValue);
        } else if (column === UserColumns.IsAdmin) {
          setIsStatusAdmin(newValue);
        }

        // Refetch the user data to ensure the UI is up-to-date with the server state
        refetch();
      },
    });
  };

  const renderPlaceList = (values: string[]) => values.map((item: string) => <Tag key={item} value={item} />);

  // Show loading indicator while fetching data
  if (isLoading) {
    return <ProgressBar mode="indeterminate" style={{ height: '3px' }} />;
  }

  return (
    <>
      <Messages ref={messages} />
      <InfoGrid>
        <span>Name:</span>
        <strong>{data?.name || data?.userInfo?.givenName}</strong>

        <span>Family Name:</span>
        <strong>{data?.familyName || data?.userInfo?.surname}</strong>

        <span>E-mail:</span>
        <strong>{data?.email}</strong>

        <span>Job Title:</span>
        <strong>{data?.userInfo?.jobTitle}</strong>

        <span>Office:</span>
        <strong>{data?.userInfo?.officeLocation}</strong>

        <span>Phone:</span>
        <strong>
          {data?.userInfo && [data.userInfo.mobilePhone, ...data.userInfo.businessPhones].filter(Boolean).join(', ')}
        </strong>

        <StatusContainer>
          <div>
            <LabelWrapper>Is Active:</LabelWrapper>
            {isStatusActive !== null && (
              <SwitchWrapper>
                <span>No</span>
                <InputSwitch
                  checked={isStatusActive}
                  onChange={(e) => handleStatusChange(UserColumns.IsActive, e.value)}
                />
                <span>Yes</span>
              </SwitchWrapper>
            )}
          </div>
          <div>
            <LabelWrapper>Is Admin:</LabelWrapper>
            {isStatusAdmin !== null && (
              <SwitchWrapper>
                <span>No</span>
                <InputSwitch
                  checked={isStatusAdmin}
                  onChange={(e) => handleStatusChange(UserColumns.IsAdmin, e.value)}
                />
                <span>Yes</span>
              </SwitchWrapper>
            )}
          </div>
        </StatusContainer>
      </InfoGrid>
      <PageTitle as="h3">Assigned Roles</PageTitle>
      <Toolbar>
        <Button
          icon="pi pi-plus"
          onClick={() => setIsAddUserRoleModalOpen(true)}
          label="Add Role"
          size="small"
          disabled={!rolesInfo}
        />
        <AddUserRoleModal
          userId={userId}
          userRolesInfo={rolesInfo}
          open={isAddUserRoleModalOpen}
          onClose={() => setIsAddUserRoleModalOpen(false)}
        />
      </Toolbar>
      <DataTable value={data?.roles || []} loading={isLoading} rowHover paginator size="small" rows={10}>
        <Column header="Role Name" field="name" sortable />
        <Column header="Grant" field="grant.name" sortable />
        <Column header="Places" field="placeCodes" body={(rowData: Role) => renderPlaceList(rowData.placeCodes)} />
        <Column
          header="Remove Role"
          body={(rowData) => (
            <ActionsWrapper>
              <Button
                icon="pi pi-trash"
                className="p-button-secondary p-button-text"
                aria-label="Delete"
                onClick={() => removeRole(rowData)}
              />
            </ActionsWrapper>
          )}
        />
      </DataTable>
    </>
  );
}

export default UserDetailPage;
